import dayjs from 'dayjs';
import { t } from 'ttag';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { generateFullName } from '@/shared/lib';
import { formatPatientAge } from '@/entities/study';
import type { Patient, RenderNotFoundContent } from './assignDeviceDrawer.types';

/**
 * Renders the empty-result message of the device id search in the assign device drawer. An in-flight search shows a loading label, a non-empty search term with no match shows 'There is no device', and an empty term prompts the user to enter a device id.
 *
 * @param isSearchingDevice - Whether a device lookup is currently running.
 * @param searchDeviceId - Current device id search term.
 * @returns The appropriate not-found message element.
 */
const renderNotFoundContent = ({ isSearchingDevice, searchDeviceId }: RenderNotFoundContent) =>
  isSearchingDevice ? (
    <span className="not-found-content">{`${t`Loading`}...`}</span>
  ) : searchDeviceId ? (
    <span className="not-found-content">{t`There is no device`}</span>
  ) : (
    <span className="not-found-content">{t`Enter device ID to search`}</span>
  );

/**
 * Builds the patient summary line shown when assigning a device to a study. A missing patient yields the double-hyphen bullet, otherwise the age is derived as whole years between the date of birth and today and appended, formatted, after the full name and gender.
 *
 * @param patient - Patient whose name, gender and date of birth are summarized.
 * @returns Summary string of name, gender and age, or the placeholder bullet.
 */
const generatePatient = (patient: Patient) => {
  if (!patient) {
    return DOUBLE_HYPHEN_BULLET;
  }

  const patientAge = dayjs().diff(patient.dob, 'years');
  const fullName = generateFullName(patient);
  return `${fullName} (${patient.gender} - ${formatPatientAge(patientAge)} yo)`;
};

export { generatePatient, renderNotFoundContent };
